import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAppContext } from '@/contexts/AppContext';
import { SETTINGS_FALLBACKS } from '@/lib/paymentRates';
import { isBilledSeriesAccountId } from '@/lib/accountSeries';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: any;
  breakdown: any;
  onBreakdownChange: (field: string, value: string) => void;
  onSave: () => void;
  isSaving: boolean;
  // NEW (optional) props for driver notes
  driverNote?: string;
  onDriverNoteChange?: (value: string) => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  job,
  breakdown,
  onBreakdownChange,
  onSave,
  isSaving,
  driverNote = '',
  onDriverNoteChange,
}) => {
  const { paymentTypes, hoistFeeRate } = useAppContext();
  const [tmHoistCount, setTmHoistCount] = useState('0'); // Set default to 0
  const [showNote, setShowNote] = useState<boolean>(!!driverNote);

  // Use the configured per-hoist increment from the TM Hoist payment type, so the
  // driver can ONLY add the value in fixed increments (count × rate) and never an
  // arbitrary dollar figure. Falls back to the settings hoist fee rate.
  const tmHoistType = (paymentTypes || []).find(
    (pt) => (pt.name || '').trim().toLowerCase() === 'tm hoist'
  );
  const tmHoistRate =
    (tmHoistType?.isPerUnit && tmHoistType.perUnitAmount) ||
    hoistFeeRate ||
    SETTINGS_FALLBACKS.tmHoist;

  // Helper to truncate pickup address to first comma
  const formatPickupLocation = (location?: string | null) => {
    if (!location) return 'N/A';
    const [firstPart] = location.split(',');
    return firstPart.trim();
  };

  // Ensure all numeric fields are parsed correctly
  const parseValue = (value: any) => parseFloat(value) || 0;

  // Calculate the total from the breakdown
  const getTotal = () => {
    if (!breakdown) return 0;
    return Object.values(breakdown).reduce((sum: number, val: any) => sum + parseValue(val), 0);
  };

  // Calculate the difference
  const getDifference = () => {
    const count = parseInt(tmHoistCount) || 0;
    const additionalAmount = count * tmHoistRate;
    const adjustedTotal = (job?.trip_total || 0) + additionalAmount;
    return adjustedTotal - getTotal();
  };

  const handleTmHoistCountChange = (value: string) => {
    setTmHoistCount(value);
    const count = parseInt(value) || 0;
    const totalAmount = count * tmHoistRate;
    onBreakdownChange('tmHoist', totalAmount.toString());
  };

  // Core fixed fields wired to the existing breakdown columns.
  const fixedFields = [
    { key: 'cash', label: 'Cash' },
    { key: 'eftpos', label: 'EFTPOS' },
    { key: 'tm', label: 'TM' },
    { key: 'tmHoist', label: 'TM Hoist', showInput: true },
    { key: 'acc', label: 'A/C' },
    { key: 'other', label: 'Other' },
    { key: 'discount', label: 'Discount' },
    { key: 'eCard', label: 'E-Card' },
  ];

  // Names already represented by a fixed field (so we don't duplicate them).
  const KNOWN_NAMES = new Set([
    'cash',
    'eftpos',
    'tm',
    'tm subsidy',
    'tm hoist',
    'tmhoist',
    'a/c',
    'acc',
    'account',
    'other',
    'discount',
    'e-card',
    'ecard',
    'e card',
  ]);

  // Build the full set of fields: the fixed ones plus any *new* payment types
  // configured in Settings → Payment Types that aren't already covered. This
  // makes the modal automatically update when a new payment method is added.
  const paymentFields = useMemo(() => {
    const dynamic = (paymentTypes || [])
      .filter((pt) => !KNOWN_NAMES.has((pt.name || '').trim().toLowerCase()))
      .map((pt) => ({ key: `pt_${pt.id}`, label: pt.name }));
    return [...fixedFields, ...dynamic];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTypes]);

  // Account ID only applies to the billed CRM series (see src/lib/accountSeries.ts)
  const shouldShowAccountId = (accountId: string) => isBilledSeriesAccountId(accountId);

  // Handle input fields
  const handleInputChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    onBreakdownChange(field, numValue.toString());
  };

  const handleNoteChange = (value: string) => {
    if (onDriverNoteChange) {
      onDriverNoteChange(value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md mx-auto p-4 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Payment Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Passenger:</span>
              <span>{job?.Passenger_First_Name || 'N/A'}</span>
            </div>
            {shouldShowAccountId(job?.Account_ID) && (
              <div className="flex justify-between">
                <span className="font-medium">Account ID:</span>
                <span>{job?.Account_ID}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="font-medium">Pick Up:</span>
              <span className="text-right text-sm">
                {formatPickupLocation(job?.Pick_Up_Location)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Total:</span>
              <span className="font-semibold">
                ${(job?.trip_total || 0).toFixed(2)}
              </span>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            {paymentFields.map((field) => (
              <div key={field.key} className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Label htmlFor={field.key} className="font-medium">
                    {field.label}:
                  </Label>
                  {field.showInput && (
                    <Input
                      type="number"
                      min="0"
                      max="9"
                      step="1"
                      value={tmHoistCount}
                      onChange={(e) => handleTmHoistCountChange(e.target.value)}
                      className="w-14 h-7 text-center text-xs"
                      placeholder="0"
                    />
                  )}
                </div>
                <Input
                  id={field.key}
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  readOnly={field.showInput}
                  value={
                    field.showInput
                      ? ((parseInt(tmHoistCount) || 0) * tmHoistRate).toFixed(2)
                      : breakdown?.[field.key] ?? '0'
                  }
                  onChange={(e) =>
                    field.showInput
                      ? undefined
                      : handleInputChange(field.key, e.target.value)
                  }
                  className={`w-24 h-8 text-right ${
                    field.showInput ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="0"
                />
              </div>
            ))}
          </div>

          {/* Note toggle + textarea (under E-Card, before Difference) */}
          <div className="pt-2 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={showNote}
                onChange={(e) => setShowNote(e.target.checked)}
              />
              <span>Add note about this job</span>
            </label>

            {showNote && (
              <textarea
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add any comments about payment, issues on the job, or special circumstances..."
                value={driverNote}
                onChange={(e) => handleNoteChange(e.target.value)}
              />
            )}
          </div>

          <Separator />

          <div className="flex justify-between items-center font-semibold">
            <span>Difference:</span>
            <span
              className={`${
                Math.abs(getDifference()) < 0.01 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              ${getDifference().toFixed(2)}
            </span>
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={isSaving || Math.abs(getDifference()) > 0.01}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
